import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  IAutomationVariable,
} from '../../../../../../redux/reducers/automationSlice';
import {
  AutomationContainer,
  AutomationHeader,
  Counter,
  VariablesList,
  VariableRow,
  VariableHeader,
  ReasonsList,
  ReasonItem,
  CommentItem,
} from './styles';
import positiveIcon from '../../../../../../assets/icons/positive.svg';
import negativeIcon from '../../../../../../assets/icons/negative.svg';
import pendingIcon from '../../../../../../assets/icons/pending-dot.svg';
import automatedIcon from '../../../../../../assets/icons/lightning.svg';

const STATUS_LABELS: Record<IAutomationVariable['status'], string> = {
  approved: 'Aprobado',
  rejected: 'Rechazado',
  pending: 'Pendiente',
};

const STATUS_ICONS: Record<IAutomationVariable['status'], string> = {
  approved: positiveIcon,
  rejected: negativeIcon,
  pending: pendingIcon,
};

const VARIABLE_LABELS: Record<string, string> = {
  final_price_alert: 'Alertas precios del comité final y Hubspot',
  revenue_low: 'Revenue demasiado bajo',
  bathroom_room_typology: 'Tipología Habitaciones y Baños',
  photos: 'Fotos',
  good_zone: 'Zona buena',
  garage_typology: 'Tipologías de Estacionamiento',
  pricing_according_zone: 'Pricing Acorde a Zona',
  alert_final_price: 'Alerta precio final',
  elevator_typology: 'Tipologías de Elevadores',
  punishment_antiquity_commite_automatic: 'Castigo por antigüedad',
  floor_elevator: 'Piso y ascensores',
  high_level_report: 'Reporte High Level',
  remodeling_comment: 'Comentarios Remodelación',
  administration: 'Administración',
  blacklist: 'Blacklist',
  risk_threats: 'Riesgo / Amenazas',
  committee_hubspot_price_alert: 'Alerta precio manual vs Ask Price',
  property_view: 'Vista',
  estructural_damage: 'Daños estructurales por sismo 2017',
  infonavit_fovissste_normative: 'Normativa del Infonavit o Fovissste',
  area: 'Tipología de Área',
  lemon_limit: 'Tope de Precio en la zona',
  // Provienen de video_vo, no de COUNTRY_VARIABLES.
  without_fence_or_private_security: 'Cerramiento y vigilancia privada',
  risk_zone: 'Inmueble en zona de riesgo',
};

const VariableItem = ({ variable }: { variable: IAutomationVariable }) => {
  const [open, setOpen] = useState(false);
  const reasons =
    variable.status === 'approved'
      ? variable.reviewed_reasons
      : variable.rejection_reasons;
  // El comment también se despliega, así que una variable sin razones pero con
  // comentario sigue siendo expandible.
  const hasDetails = reasons.length > 0 || Boolean(variable.comment);

  return (
    <VariableRow>
      <VariableHeader onClick={() => hasDetails && setOpen(prev => !prev)}>
        <div className="variable-left">
          {hasDetails && (
            <span className={`chevron ${open ? 'open' : ''}`}>▶</span>
          )}
          {!hasDetails && <span style={{ width: 14 }} />}
          <span className="variable-name">
            {VARIABLE_LABELS[variable.variable] || variable.variable}
          </span>
        </div>
        <div className={`status-badge ${variable.status}`}>
          <img
            src={STATUS_ICONS[variable.status]}
            alt={variable.status}
            width={14}
            height={14}
          />
          {STATUS_LABELS[variable.status]}
        </div>
      </VariableHeader>
      {open && hasDetails && (
        <ReasonsList>
          {reasons.map(reason => (
            <ReasonItem key={reason.value}>{reason.label}</ReasonItem>
          ))}
          {variable.comment && (
            <CommentItem data-testid={`comment-${variable.variable}`}>
              {variable.comment}
            </CommentItem>
          )}
        </ReasonsList>
      )}
    </VariableRow>
  );
};

const AutomationSummary = () => {
  const [expanded, setExpanded] = useState(true);
  const summary = useSelector(
    (state: any) => state.automationStore?.summary ?? { approved: 0, rejected: 0, pending: 0 }
  );
  const variables: IAutomationVariable[] = useSelector(
    (state: any) => state.automationStore?.variables ?? []
  );

  // Las pendientes se ocultan salvo que traigan comentario: ese es el único
  // dato útil que el servicio entrega antes de resolver la variable.
  const visibleVariables = variables.filter(
    v => v.status !== 'pending' || Boolean(v.comment)
  );

  if (visibleVariables.length === 0) return null;

  return (
    <AutomationContainer>
      <AutomationHeader onClick={() => setExpanded(prev => !prev)}>
        <div className="left">
          <img src={automatedIcon} alt="automatizado" width={20} height={20} />
          <span className="title">Revisión automática</span>
        </div>
        <div className="counters">
          <Counter variant="approved">
            <img src={positiveIcon} alt="aprobadas" width={14} height={14} />
            {summary.approved} aprobadas
          </Counter>
          <Counter variant="rejected">
            <img src={negativeIcon} alt="rechazadas" width={14} height={14} />
            {summary.rejected} rechazadas
          </Counter>
          <Counter variant="pending">{summary.pending} pendientes</Counter>
          <span style={{ fontSize: 12, color: '#999' }}>
            {expanded ? '▲' : '▼'}
          </span>
        </div>
      </AutomationHeader>
      {expanded && (
        <VariablesList>
          {visibleVariables.map(variable => (
            <VariableItem key={variable.variable} variable={variable} />
          ))}
        </VariablesList>
      )}
    </AutomationContainer>
  );
};

export default AutomationSummary;
